import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, PenLine, Trash2, ShoppingBag, FileText } from 'lucide-react';
import Layout from '../../../../components/Layout';
import { customerService, CustomerDTO } from '../../../../services/customerService';
import '../../../../pages/Products/ProductCategoryDetailPage.css';

const CustomerDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [customer, setCustomer] = useState<CustomerDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCustomer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadCustomer = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await customerService.getCustomerById(id || '0');
      if (response?.data?.data) {
        setCustomer(response.data.data);
      }
      setLoading(false);
    } catch {
      setError(t('customer.failedToLoadCustomer'));
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="detail-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!customer) {
    return (
      <Layout>
        <div className="detail-container">
          <div className="alert alert-danger">{error || t('customer.customerNotFound')}</div>
          <button className="detail-breadcrumb-btn" onClick={() => navigate('/customers')}>
            {t('customer.backToCustomers')}
          </button>
        </div>
      </Layout>
    );
  }

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';

  return (
    <Layout>
      <div className="detail-container">
        <nav className="detail-breadcrumb">
          <button
            className="detail-breadcrumb-btn"
            onClick={() => navigate('/customers')}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('customer.backToCustomers')}
          </button>
        </nav>

        <div className="detail-header">
          <div>
            <h1 className="detail-title">{customer.name}</h1>
            <div className="detail-meta">
              <span className="detail-meta-item">
                <code>{customer.code}</code>
              </span>
              <span className="detail-meta-item">
                <span className={`badge ${customer.isActive ? 'badge-active' : 'badge-inactive'}`}>
                  {customer.isActive ? t('customer.active') : t('customer.inactive')}
                </span>
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <div className="detail-content">
          <div className="detail-main-section">

            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('customer.customerInfo')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <span className="detail-label">{t('customer.customerCode')}</span>
                    <span className="detail-value"><code>{customer.code}</code></span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('customer.customerName')}</span>
                    <span className="detail-value">{customer.name}</span>
                  </div>
                  {customer.customerType && (
                    <div className="detail-field">
                      <span className="detail-label">{t('customer.customerType')}</span>
                      <span className="detail-value">{customer.customerType}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('customer.contactDetails')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <span className="detail-label">{t('customer.contactEmail')}</span>
                    <span className="detail-value">{customer.contactEmail}</span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('customer.contactPhone')}</span>
                    <span className="detail-value">{customer.contactPhone || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>

            {(customer.billingAddress || customer.shippingAddress || customer.city || customer.state || customer.postalCode || customer.country) && (
              <div className="detail-card">
                <div className="detail-card-header">
                  <h2 className="detail-card-title">{t('customer.addressDetails')}</h2>
                </div>
                <div className="detail-card-body">
                  <div className="detail-grid">
                    {customer.billingAddress && (
                      <div className="detail-field">
                        <span className="detail-label">{t('customer.billingAddress')}</span>
                        <span className="detail-value">{customer.billingAddress}</span>
                      </div>
                    )}
                    {customer.shippingAddress && (
                      <div className="detail-field">
                        <span className="detail-label">{t('customer.shippingAddress')}</span>
                        <span className="detail-value">{customer.shippingAddress}</span>
                      </div>
                    )}
                    {customer.city && (
                      <div className="detail-field">
                        <span className="detail-label">{t('customer.city')}</span>
                        <span className="detail-value">{customer.city}</span>
                      </div>
                    )}
                    {customer.state && (
                      <div className="detail-field">
                        <span className="detail-label">{t('customer.state')}</span>
                        <span className="detail-value">{customer.state}</span>
                      </div>
                    )}
                    {customer.postalCode && (
                      <div className="detail-field">
                        <span className="detail-label">{t('customer.postalCode')}</span>
                        <span className="detail-value">{customer.postalCode}</span>
                      </div>
                    )}
                    {customer.country && (
                      <div className="detail-field">
                        <span className="detail-label">{t('customer.country')}</span>
                        <span className="detail-value">{customer.country}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {(customer.taxId !== undefined || customer.creditLimit !== undefined || customer.paymentTerms) && (
              <div className="detail-card">
                <div className="detail-card-header">
                  <h2 className="detail-card-title">{t('customer.businessInformation')}</h2>
                </div>
                <div className="detail-card-body">
                  <div className="detail-grid">
                    {customer.taxId && (
                      <div className="detail-field">
                        <span className="detail-label">{t('customer.taxId')}</span>
                        <span className="detail-value">{customer.taxId}</span>
                      </div>
                    )}
                    {customer.creditLimit !== undefined && (
                      <div className="detail-field">
                        <span className="detail-label">{t('customer.creditLimit')}</span>
                        <span className="detail-value">${customer.creditLimit?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    {customer.paymentTerms && (
                      <div className="detail-field">
                        <span className="detail-label">{t('customer.paymentTerms')}</span>
                        <span className="detail-value">{customer.paymentTerms}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('customer.auditInformation')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <span className="detail-label">{t('customer.customerId')}</span>
                    <span className="detail-value">{customer.id}</span>
                  </div>
                  <div className="detail-field">
                    <span className="detail-label">{t('customer.createdDate')}</span>
                    <span className="detail-value">
                      {new Date(customer.createdAt).toLocaleDateString(langCode)}{' '}
                      {new Date(customer.createdAt).toLocaleTimeString(langCode)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <div className="detail-sidebar">
            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">{t('common.actions')}</h3>
              </div>
              <div className="detail-card-body">
                <div className="detail-actions">
                  <button
                    className="btn-save"
                    onClick={() => navigate(`/customers/${customer.id}/edit`)}
                  >
                    <PenLine size={16} strokeWidth={2} />
                    {t('customer.editCustomer')}
                  </button>
                  <button
                    className="btn-delete"
                    onClick={() => navigate(`/customers/${customer.id}/delete`)}
                  >
                    <Trash2 size={16} strokeWidth={2} />
                    {t('common.delete')}
                  </button>
                  <button
                    className="detail-btn detail-btn-secondary"
                    onClick={() => navigate('/customers')}
                  >
                    <ArrowLeft size={16} strokeWidth={2} />
                    {t('customer.backToCustomers')}
                  </button>
                </div>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">{t('customer.quickActions')}</h3>
              </div>
              <div className="detail-card-body">
                <button
                  className="detail-quick-link"
                  onClick={() => navigate(`/sales-items?customerId=${customer.id}`)}
                >
                  <ShoppingBag size={16} strokeWidth={2} />
                  {t('customer.viewSalesItems')}
                </button>
                <button
                  className="detail-quick-link"
                  onClick={() => navigate('/sales/sales-invoices/create', { state: { customerId: customer.id } })}
                > 
                  <FileText size={16} strokeWidth={2} />
                  {t('salesInvoice.createInvoice')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CustomerDetailPage;
